import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Camera, UploadSimple, ShieldCheck, WarningCircle, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { readExif, describeExif, matchCameraId, lensFromExif } from "@/utils/exif";

export default function ExifReader() {
  const { lang } = useTranslation();
  const es = lang === "es";
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [data, setData] = useState(undefined);
  const [reading, setReading] = useState(false);
  const [cameras, setCameras] = useState([]);

  useEffect(() => {
    axios.get("/api/cameras").then((r) => setCameras(r.data)).catch(() => {});
  }, []);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setPreview(URL.createObjectURL(file));
    setReading(true);
    try {
      setData(await readExif(file));
    } catch {
      setData(null);
    }
    setReading(false);
    e.target.value = "";
  };

  const matchedId = data ? matchCameraId(data.Model, cameras) : null;
  const rows = data ? describeExif(data, lang).filter((r) => r.value) : [];

  const useInCalculator = () => {
    const lens = lensFromExif(data);
    const prefill = { ...(matchedId ? { camera_id: matchedId } : {}) };
    if (lens.focal_length) prefill.focal_length = lens.focal_length;
    if (lens.max_aperture) prefill.max_aperture = lens.max_aperture;
    navigate("/", { state: { exifPrefill: prefill } });
  };

  return (
    <div className="space-y-4" data-testid="exif-reader">
      <div className="flex items-center gap-3 mb-2">
        <Camera weight="fill" className="w-6 h-6 text-[#F5A524]" />
        <div>
          <h3 className="font-['Outfit'] text-lg text-white font-medium">{es ? "Lector EXIF" : "EXIF Reader"}</h3>
          <p className="text-[#71717A] text-sm">
            {es ? "Elige una foto y descubre con qué ajustes fue tomada." : "Pick a photo and see the settings it was taken with."}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input ref={inputRef} type="file" accept="image/*,.heic,.heif,.dng,.cr2,.nef,.arw" className="hidden" onChange={onPick} data-testid="exif-file-input" />
        <Button onClick={() => inputRef.current?.click()} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium" data-testid="exif-pick-btn">
          <UploadSimple weight="bold" className="w-4 h-4 mr-1.5" />
          {es ? "Elegir foto" : "Choose photo"}
        </Button>
        <span className="flex items-center gap-1.5 text-[#71717A] text-xs">
          <ShieldCheck weight="fill" className="w-4 h-4 text-emerald-400" />
          {es ? "Se lee solo en tu navegador. Nunca se sube ni se guarda." : "Read in your browser only. Never uploaded or stored."}
        </span>
      </div>

      {preview && (
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden self-start">
            <img src={preview} alt={fileName} className="w-full h-auto object-contain max-h-60" data-testid="exif-preview" />
            <p className="text-[#71717A] text-[11px] px-2 py-1.5 truncate">{fileName}</p>
          </div>
          {reading ? (
            <p className="text-[#A1A1AA] text-sm" data-testid="exif-reading">{es ? "Leyendo..." : "Reading..."}</p>
          ) : data ? (
            <ExifResults rows={rows} matched={!!matchedId} es={es} onUse={useInCalculator} />
          ) : (
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 flex items-start gap-3" data-testid="exif-no-data">
              <WarningCircle weight="fill" className="w-5 h-5 text-[#F5A524] shrink-0 mt-0.5" />
              <p className="text-[#A1A1AA] text-sm">
                {es
                  ? "No encontramos datos de ajustes en esta foto. Muchos PNG, capturas de pantalla e imágenes de redes sociales no los conservan. Prueba con el JPG o RAW original de tu cámara."
                  : "No settings data found in this photo. Most PNGs, screenshots and social-media images don't keep it. Try the original JPG or RAW from your camera."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExifResults({ rows, matched, es, onUse }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="exif-results">
        {rows.map((r) => (
          <div key={r.key} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 min-w-0">
            <div className="text-[#71717A] text-[10px] uppercase tracking-[0.15em]">{r.label}</div>
            <div className="text-white text-sm font-['JetBrains_Mono'] break-words" data-testid={`exif-${r.key}`}>{r.value}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onUse} variant="outline" className="border-[#F5A524]/40 text-[#F5A524] hover:bg-[#F5A524]/10" data-testid="exif-use-calculator-btn">
          {es ? "Usar en el Calculador" : "Use in Settings Calculator"}
          <ArrowRight weight="bold" className="w-4 h-4 ml-1.5" />
        </Button>
        {!matched && (
          <span className="text-[#71717A] text-xs" data-testid="exif-camera-not-listed">
            {es ? "Esta cámara no está en la lista: se copiarán la focal y la apertura; elige tu cámara a mano." : "This camera isn't in our list: focal length and aperture will be copied; pick your camera manually."}
          </span>
        )}
      </div>
    </div>
  );
}
